import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ordersApi } from "../services/orders";
import type { Receipt, ReceiptStatus } from "../types/orders";
import { INSTAGRAM_URL } from "../constants/links";
import "./ReceiptPage.css";
import { formatSingleAmount } from "../utils/currency";
import { publicUrl } from "../utils/publicUrl";
import { t, formatLocale } from "../i18n";

// Inline Instagram glyph — keeps the receipt page dependency-free (no icon
// library, no remote SVG fetch). currentColor lets the parent <a> control fill.
const InstagramIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    focusable={false}
  >
    <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

// Public, permanent receipt page (Phase 2 PR A).
//
// Reached at /receipt/:receiptId. The URL itself IS the access token —
// anyone with the link can view the receipt, anyone without it cannot.
// There is no auth here on purpose: this is what customers save, screenshot,
// and forward to their bank when a dispute happens.
//
// The page is intentionally static (no polling, no confetti, no upsells).
// It's a record, not an experience. The dynamic post-checkout flow still
// lives at /success?order_id=... — that's a different product.

// Phase C: shared util handles all 12 pay-in currencies with correct
// symbol placement ("$20.00" / "200.00 kr").
const formatMoney = (amount: number | null, currency: string): string => {
  if (amount == null) return "—";
  return formatSingleAmount(amount, currency);
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString(formatLocale(), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = d.toLocaleTimeString(formatLocale(), {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date} · ${time}`;
};

const STATUS_LABEL: Record<ReceiptStatus, string> = {
  pending: t("receiptPage.awaitingTalent"),
  queued: t("receiptPage.accepted"),
  accepted: t("receiptPage.accepted"),
  played: t("receiptPage.played"),
  declined: t("receiptPage.declined"),
  refunded: t("receiptPage.refunded"),
  frozen: t("receiptPage.onHold"),
};

const STATUS_CLASS: Record<ReceiptStatus, string> = {
  pending: "receipt-status--neutral",
  queued: "receipt-status--positive",
  accepted: "receipt-status--positive",
  played: "receipt-status--positive",
  declined: "receipt-status--negative",
  refunded: "receipt-status--neutral",
  frozen: "receipt-status--warning",
};

const ReceiptPage: React.FC = () => {
  const { receiptId } = useParams<{ receiptId: string }>();
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "not-found" | "error">(
    "loading"
  );
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");

  useEffect(() => {
    let cancelled = false;
    if (!receiptId) {
      setStatus("not-found");
      return;
    }
    ordersApi
      .receipt(receiptId)
      .then((res) => {
        if (cancelled) return;
        setReceipt(res);
        setStatus("ok");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setStatus("not-found");
        } else {
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [receiptId]);

  const handlePrint = () => {
    window.print();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl());
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1600);
    } catch {
      // Clipboard API can fail in insecure contexts or if user denies — silently no-op.
    }
  };

  if (status === "loading") {
    return (
      <div className="receipt-page">
        <div className="receipt-card receipt-card--message">
          <p className="receipt-loading" role="status" aria-live="polite">
            {t("receiptPage.loadingReceipt")}
          </p>
        </div>
      </div>
    );
  }

  if (status === "not-found") {
    return (
      <div className="receipt-page">
        <div className="receipt-card receipt-card--message">
          <h1 className="receipt-message-title">{t("receiptPage.receiptNotFound")}</h1>
          <p className="receipt-message-body">
            {t("receiptPage.thisReceiptLinkIs")}
          </p>
          <Link to="/" className="receipt-button receipt-button--secondary">
            {t("receiptPage.backToHome")}
          </Link>
        </div>
      </div>
    );
  }

  if (status === "error" || !receipt) {
    return (
      <div className="receipt-page">
        <div className="receipt-card receipt-card--message">
          <h1 className="receipt-message-title">{t("receiptPage.somethingWentWrong")}</h1>
          <p className="receipt-message-body">
            {t("receiptPage.weCouldnTLoad")}
          </p>
        </div>
      </div>
    );
  }

  const artistsLabel =
    receipt.trackArtists && receipt.trackArtists.length > 0
      ? receipt.trackArtists.join(", ")
      : null;
  const amountToShow =
    receipt.totalAmount != null ? receipt.totalAmount : receipt.amount;
  const statusLabel = STATUS_LABEL[receipt.status] || receipt.status;
  const statusClass = STATUS_CLASS[receipt.status] || "receipt-status--neutral";

  // Build the timeline chronologically. Two events only:
  //   - Ordered (createdAt) — when the customer placed the request
  //   - Played (playedAt) — when the DJ tapped Play
  // For legacy orders without playedAt, the row still renders with "—" so the
  // customer can see the order date AND see that play hasn't been stamped.
  // PR B populates playedAt going forward.
  const timeline: { label: string; iso: string | null }[] = [
    { label: t("receiptPage.ordered"), iso: receipt.createdAt },
    { label: t("receiptPage.played"), iso: receipt.playedAt },
  ];
  if (receipt.refundedAt) {
    timeline.push({ label: t("receiptPage.refunded"), iso: receipt.refundedAt });
  }

  return (
    <div className="receipt-page">
      <div className="receipt-card">
        <header className="receipt-header">
          <p className="receipt-brand">{t("receiptPage.requestApp")}</p>
          <h1 className="receipt-title">{t("receiptPage.receipt")}</h1>
        </header>

        <section className="receipt-track">
          <div className="receipt-track-image-wrap">
            {receipt.trackImage ? (
              <img
                src={receipt.trackImage}
                alt={receipt.songName || t("receiptPage.trackArtwork")}
                className="receipt-track-image"
              />
            ) : (
              <div className="receipt-track-image-placeholder" aria-hidden />
            )}
          </div>
          <h2 className="receipt-song-title">{receipt.songName || "—"}</h2>
          {artistsLabel && (
            <p className="receipt-song-artists">{artistsLabel}</p>
          )}
        </section>

        <section className="receipt-amount-block">
          <p className="receipt-amount">
            {formatMoney(amountToShow, receipt.currency)}
          </p>
          <span className={`receipt-status ${statusClass}`}>{statusLabel}</span>
        </section>

        <section className="receipt-meta">
          {receipt.talentName && (
            <div className="receipt-meta-row">
              <span className="receipt-meta-label">{t("receiptPage.talent")}</span>
              <span className="receipt-meta-value">{receipt.talentName}</span>
            </div>
          )}
          {receipt.venueAtPlayed && (
            <div className="receipt-meta-row">
              <span className="receipt-meta-label">{t("receiptPage.venue")}</span>
              <span className="receipt-meta-value">{receipt.venueAtPlayed}</span>
            </div>
          )}
        </section>

        <section className="receipt-timeline">
          {timeline.map((step, i) => (
            <div key={`${step.label}-${i}`} className="receipt-timeline-row">
              <span className="receipt-timeline-label">{step.label}</span>
              <span className="receipt-timeline-value">
                {formatDateTime(step.iso)}
              </span>
            </div>
          ))}
        </section>

        <section className="receipt-actions">
          <button
            type="button"
            className="receipt-button receipt-button--primary"
            onClick={handleCopyLink}
            aria-live="polite"
          >
            {copyState === "copied" ? t("receiptPage.linkCopied") : t("receiptPage.copyLink")}
          </button>
          <button
            type="button"
            className="receipt-button receipt-button--secondary"
            onClick={handlePrint}
          >
            {t("receiptPage.printReceipt")}
          </button>
          {receipt.djSlug && (
            <Link
              to={`/${receipt.djSlug}`}
              className="receipt-button receipt-button--ghost"
            >
              {t("receiptPage.requestAnotherSong")}
            </Link>
          )}
        </section>

        <footer className="receipt-footer">
          <p className="receipt-footer-line">
            <strong>{t("receiptPage.requestaLlc")}</strong>
          </p>
          <p className="receipt-footer-socials">
            <a
              className="receipt-footer-iconlink"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("receiptPage.requestAppOnInstagram")}
            >
              <InstagramIcon />
            </a>
          </p>
          <p className="receipt-footer-line">
            <a
              className="receipt-footer-link"
              href="mailto:support@requesta.app"
            >
              {t("receiptPage.supportRequestaApp")}
            </a>
          </p>
          <p className="receipt-footer-id">{t("receiptPage.receiptId", { receiptId: receipt.receiptId })}</p>
        </footer>
      </div>
    </div>
  );
};

export default ReceiptPage;
